'use client';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/hooks/useAuth';
import { useClobClient } from '@/hooks/useClobClient';
import axiosClient from '@/services/api/axiosClient';

export const ORDERS_QUERY_KEYS = {
  all: ['clob-orders'] as const,
  open: (address?: string) => ['clob-orders', 'open', address ?? ''] as const,
};

/**
 * Fetch open orders for the current user from Polymarket CLOB API.
 * Uses the existing clobClient (which already has signed credentials).
 * Equivalent to: GET https://clob.polymarket.com/orders
 */
export const useOpenOrders = () => {
  const { user } = useAuth();
  const clobClient = useClobClient();
  const address = user?.proxyWalletAddress;

  return useQuery({
    queryKey: ORDERS_QUERY_KEYS.open(address),
    queryFn: async () => {
      if (!clobClient) throw new Error('CLOB client not ready');

      const response = await clobClient.getOpenOrders();

      const ordersArray = Array.isArray(response) ? response : (response as any).data || [];
      if (!ordersArray || ordersArray.length === 0) return response;

      // Extract unique asset IDs (CLOB token IDs)
      const clobTokenIds = Array.from(
        new Set(ordersArray.map((o: any) => o.asset_id).filter(Boolean))
      );

      if (clobTokenIds.length > 0) {
        try {
          // Call the backend Gamma proxy to bypass Gamma CORS origin blocks
          const gammaRes = await axiosClient.post('api/v1/markets/gamma', { clobTokenIds });

          if (gammaRes.status === 200) {
            const gammaData = gammaRes.data;

            // Map token_id to market info
            const tokenMap = new Map();
            if (Array.isArray(gammaData)) {
              gammaData.forEach((market: any) => {
                try {
                  const clobIds =
                    typeof market.clobTokenIds === 'string'
                      ? JSON.parse(market.clobTokenIds)
                      : market.clobTokenIds || [];

                  const outcomes =
                    typeof market.outcomes === 'string'
                      ? JSON.parse(market.outcomes)
                      : market.outcomes || [];

                  clobIds.forEach((tokenId: string, index: number) => {
                    tokenMap.set(tokenId, {
                      title: market.question,
                      icon: market.image || market.icon,
                      outcome: outcomes[index],
                    });
                  });
                } catch (err) {
                  console.error('[useOpenOrders] Error parsing market arrays', err);
                }
              });
            }

            // Enrich the orders
            const enrichedOrders = ordersArray.map((o: any) => {
              const info = tokenMap.get(o.asset_id);
              if (info) {
                return {
                  ...o,
                  market: info.title,
                  icon: info.icon,
                  outcome: info.outcome || o.outcome,
                };
              }
              return o;
            });

            const finalResponse = Array.isArray(response)
              ? enrichedOrders
              : { ...(response as any), data: enrichedOrders };
            return finalResponse;
          }
        } catch (e) {
          console.error('[useOpenOrders] Failed to fetch market info from Gamma:', e);
        }
      }
      return response;
    },
    enabled: !!clobClient && !!address,
    staleTime: 10_000,
    refetchInterval: 10_000,
  });
};
